import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { CustomNumberFormatPipe, EThemeName, ITimesTemp, ThemeService } from '@wind-log_library/fe-component-library';
import { setKpiSummaryMonthTableSubName } from 'app/shared/helper/kpi.helper';
import moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import { CS_BAR_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { DecimalPipe } from '@angular/common';
import { isNil } from 'lodash';
import { getUnitLabel, getUnitSuffix, isPercentUnit, toUnitValue } from 'app/shared/helper/kpi-units.helper';

@Component({
  selector: 'app-kpi-availability-monthly-chart',
  templateUrl: './kpi-availability-monthly-chart.component.html',
  styleUrls: ['./kpi-availability-monthly-chart.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class KpiAvailabilityMonthlyChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() availabilityService;
  @Input() availabilitySelected;
  @Input() isAvailability;
  @Input() assetId: string;
  @Input() timesTamp: ITimesTemp;
  @Input() availabilityData: any;
  @Input() loading: boolean;
  @Input() reportView: boolean = false;
  @Input() reportChartHeight: number;

  CHART_HEIGHT = 300;
  chartId = Math.random().toString(36).substring(2);
  sort = 1;
  sortList = [];
  sortListSelected = 0;
  public contractualTarget;
  public rawData: any;
  public data = [];
  private unsubscriber = new ReplaySubject();

  public colorSchemeStacked = { domain: [] };
  public yAxis = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  public yAxisPersistant = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  public theme$: Observable<EThemeName>;
  public units: string;

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

  constructor(
    private themeService: ThemeService,
    private translate: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private decimalPipe: DecimalPipe,
    private customNumberFormatPipe: CustomNumberFormatPipe,
  ) {}

  @HostListener('window:resize')
  ngOnInit() {
    this.subscribeToLanguageChange();
    this.serColorSchemeStacked();
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  round(myNumber) {
    return myNumber.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.availabilityData && changes.availabilityData.currentValue) {
      this.rawData = changes.availabilityData.currentValue;
      this.filterData();
    }
    if (
      changes.availabilitySelected &&
      (changes.availabilitySelected.currentValue || changes.availabilitySelected.currentValue === 0)
    ) {
      this.availabilitySelected = changes.availabilitySelected.currentValue;
      this.rawData ? this.filterData() : null;
    }
    this.chartOptions = this.getChartOptions();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private calculateCategoryPercentage(): number {
    const basePercentage = 0.15;
    switch (true) {
      case this.data.length === 12:
        return basePercentage;
      case this.data.length > 8:
        return (this.data.length / 8) * basePercentage;
      case this.data.length > 6:
        return (this.data.length / 6) * basePercentage;
      case this.data.length > 4:
        return (this.data.length / 4.5) * basePercentage;
      case this.data.length > 2:
        return (this.data.length / 3) * basePercentage;
      case this.data.length > 1:
        return (this.data.length / 1.25) * basePercentage;
      default:
        return basePercentage * 2.5;
    }
  }

  private getChartDatasets(): SeriesOptionsType[] {
    const actualName = this.data.map(dataset => dataset.series[0].name)[0] || '-';
    const accumulatedName = this.data.map(dataset => dataset.series[1].name)[0] || '-';
    return [
      {
        type: 'column',
        name: actualName,
        color: this.colorSchemeStacked.domain[0],
        data: this.data.map(dataset => dataset.series[0].value),
        stack: 0,
      },
      {
        type: 'column',
        name: accumulatedName,
        color: this.colorSchemeStacked.domain[1],
        data: this.data.map(dataset => dataset.series[1].value),
        stack: 1,
      },
    ];
  }

  private getValue = (value, unit = ''): string => {
    if (isNil(value)) return '-';
    return this.customNumberFormatPipe.transform(this.decimalPipe.transform(value, '1.2-2')) + unit;
  };

  private getChartOptions(): Highcharts.Options {
    const series = this.getChartDatasets();
    const getValue = this.getValue;
    const target = this.data[0]?.kpiTarget || 0;
    const targetLabel = this.translate.instant('COMMON.TARGET') || '-';
    const unitSuffix = getUnitSuffix(this.units);
    const yAxis = Highcharts.merge(CS_BAR_CHART_OPTIONS.yAxis, {
      title: {
        text: getUnitLabel(this.units),
      },
      min: 0,
      max: isPercentUnit(this.units) ? this.yAxis[this.yAxis.length - 1] : undefined,
    });
    const xAxis = Highcharts.merge(CS_BAR_CHART_OPTIONS.xAxis, {
      type: 'category',
      categories: this.data.map(d => d.name),
    });
    const chartOptions = Highcharts.merge(CS_BAR_CHART_OPTIONS, {
      chart: {
        height: this.reportView ? (this.reportChartHeight ?? this.CHART_HEIGHT) : this.CHART_HEIGHT,
        spacing: [20, 0, 0, 0],
        animation: { duration: 0 },
      },
      xAxis: xAxis,
      yAxis: yAxis,
      tooltip: {
        enabled: true,
        hideDelay: 0,
        animation: { duration: 0 },
        formatter: function () {
          const title = this.point?.category;
          const name = this.point?.series?.userOptions?.name || '-';
          if (target >= 0) {
            return `
              <div class="font-weight-bold" style="font-size: 16px; font-weight: 600;">${title}</div><br/><br/>
              ${name}: <b>${getValue(this.point.y, unitSuffix)}</b><br/>
              ${name + ' ' + targetLabel}: <b>${getValue(target, unitSuffix)}</b><br/>
            `;
          } else {
            return `
              <div class="font-weight-bold" style="font-size: 16px; font-weight: 600;">${title}</div><br/><br/>
              ${name}: <b>${getValue(this.point.y, unitSuffix)}</b><br/>
            `;
          }
        },
      },
      plotOptions: {
        column: {
          borderRadius: 0,
        },
        series: {
          groupPadding: this.calculateCategoryPercentage(),
          animation: false,
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: series,
      annotations: [
        {
          draggable: '',
          shapes: [
            {
              type: 'path',
              stroke: this.themeService.getColor('--budget-follow-up-budget'),
              strokeWidth: 1.5,
              points: [
                { x: -1, y: target, xAxis: 0, yAxis: 0 },
                { x: this.data.length, y: target, xAxis: 0, yAxis: 0 },
              ],
            },
          ],
        },
      ],
    });
    return chartOptions;
  }

  private filterData() {
    this.data = [];
    this.units = undefined;
    const index = this.availabilitySelected?.kpiIndex || this.availabilitySelected?.index;
    this.rawData.forEach(r => {
      const subName = setKpiSummaryMonthTableSubName(r.dateString, this.timesTamp.startTS, this.timesTamp.endTS);
      r.availabilities.forEach(k => {
        if (k.kpiIndex === index) {
          this.units = k.units;
          this.data.push({
            name: this.dateTimeFormatPreferencePipe.transform(moment(r.dateString).toDate(), 'my') + ' ' + subName,
            series: [
              { name: k.name, value: toUnitValue(k?.rangeResult, k.units) },
              {
                name: k.name + ' (' + this.translate.instant('KPI.SUMMARY.ACCUMULATED_SHORT') + ')',
                value: toUnitValue(k.ytdResult, k.units),
              },
            ],
            kpiTarget: toUnitValue(k.target ? k.target : -1, k.units),
          });
        }
      });
    });

    if (this.rawData && this.rawData.length > 0 && this.availabilitySelected && this.availabilitySelected.index > -1) {
      this.yAxis = [...this.yAxisPersistant];
      this.data.forEach(data => {
        const target = data.kpiTarget;
        if (this.yAxis.indexOf(target) === -1) {
          this.yAxis.push(target);
        }
      });
      this.yAxis = this.yAxis.sort((a, b) => a - b);
    } else if (this.rawData && this.isAvailability) {
      this.yAxis = [...this.yAxisPersistant];
    }
  }

  private serColorSchemeStacked(): void {
    this.theme$ = this.themeService.getTheme().pipe(
      tap(() => {
        this.colorSchemeStacked = {
          domain: [this.themeService.getColor('--primary-background'), this.themeService.getColor('--secondary')],
        };
        this.chartOptions = this.getChartOptions();
      }),
    );
  }

  private subscribeToLanguageChange(): void {
    this.translate.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      this.filterData();
      this.chartOptions = this.getChartOptions();
    });
  }
}
